import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { DEMO_ENABLED } from "../lib/demo";
import { ATOMIKA_NAVY } from "../lib/maelabord/brand";
import { api } from "../lib/api";
import type { Customer } from "../lib/markadur";
import type { MaelabordTabId } from "../components/maelabord/types";
import { DemoBadge, DemoOff } from "../components/maelabord/DemoOff";
import { YfirlitTab } from "../components/maelabord/tabs/YfirlitTab";
import { HitakerfiTab } from "../components/maelabord/tabs/HitakerfiTab";
import { StjornturnTab } from "../components/maelabord/tabs/StjornturnTab";
import { FerlidTab } from "../components/maelabord/tabs/FerlidTab";
import { FravikakerfiTab } from "../components/maelabord/tabs/FravikakerfiTab";

export const Route = createFileRoute("/verkfaeri/atomika-maelabord")({
  component: MaelabordShell,
});

interface TabDef {
  id: MaelabordTabId;
  label: string;
  icon: string;
  /** Demo-drifinn flipi — birtir DemoOff þegar slökkt er á sýnigögnum. */
  demo?: boolean;
}

const TABS: TabDef[] = [
  { id: "yfirlit", label: "Yfirlit", icon: "🏠", demo: true },
  { id: "hitakerfi", label: "Hitakerfi", icon: "⚡" },
  { id: "stjornturn", label: "Stjórnturn", icon: "🛰️", demo: true },
  { id: "fravikakerfi", label: "Frávikakerfi", icon: "🛡️", demo: true },
  { id: "ferlid", label: "Ferlið", icon: "🧭" },
];

/** Reads the initial customer kennitala from the URL (?customer=…) once. */
function initialCustomerId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return (
    new URLSearchParams(window.location.search).get("customer") ?? undefined
  );
}

function MaelabordShell() {
  const [customerId, setCustomerId] = useState<string | undefined>(
    initialCustomerId,
  );
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [tab, setTab] = useState<MaelabordTabId>(
    initialCustomerId() ? "hitakerfi" : "yfirlit",
  );
  const displayName = "";

  const anyDemo = TABS.some((t) => t.demo);

  useEffect(() => {
    api
      .getMarkadurCustomers(true)
      .then((r) => setCustomers(r.customers))
      .catch(() => {});
  }, []);

  const selected = customers.find((c) => c.kennitala === customerId) ?? null;

  return (
    <div className="mx-auto w-full max-w-[1320px] px-4 pb-16 pt-6 sm:px-6">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink">
          Atomika mælaborð
        </h1>
        {DEMO_ENABLED && anyDemo && <DemoBadge />}
        <div className="flex-1" />
        {customers.length > 0 && (
          <label className="flex items-center gap-2 text-[13px] font-semibold text-muted">
            Viðskiptavinur
            <select
              value={customerId ?? ""}
              onChange={(e) => setCustomerId(e.target.value || undefined)}
              className="rounded-lg border border-line bg-white px-3 py-1.5 text-[13px] font-semibold text-ink outline-none focus:border-brand"
            >
              <option value="">Allur floti</option>
              {customers.map((c) => (
                <option key={c.kennitala} value={c.kennitala}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {selected && (
        <div className="mb-4 rounded-xl border border-line bg-white px-4 py-2.5 text-[13px] text-muted-2">
          Sýni gögn fyrir{" "}
          <span className="font-bold text-ink">{selected.name}</span> · kt.{" "}
          {selected.kennitala}
          {selected.dashboardConnected ? " · tengt ✓" : " · engin gögn enn"}
        </div>
      )}

      <div className="mb-6 flex flex-wrap gap-1.5 rounded-xl border border-line bg-white p-1 shadow-sm">
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors ${
                active
                  ? "text-white"
                  : "text-muted hover:bg-canvas hover:text-ink"
              }`}
              style={active ? { background: ATOMIKA_NAVY } : undefined}
            >
              <span aria-hidden>{t.icon}</span>
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "yfirlit" &&
        (DEMO_ENABLED ? (
          <YfirlitTab displayName={displayName} onNavigate={setTab} />
        ) : (
          <DemoOff title="Yfirlit" />
        ))}

      {tab === "hitakerfi" && <HitakerfiTab companyId={customerId} />}

      {tab === "stjornturn" &&
        (DEMO_ENABLED ? (
          <StjornturnTab displayName={displayName} />
        ) : (
          <DemoOff title="Stjórnturn" />
        ))}

      {tab === "fravikakerfi" &&
        (DEMO_ENABLED ? <FravikakerfiTab /> : <DemoOff title="Frávikakerfi" />)}

      {tab === "ferlid" && (
        <FerlidTab displayName={displayName} onNavigate={setTab} />
      )}
    </div>
  );
}
